import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useLanguage } from './LanguageContext';
import { getTranslation } from './translations';
import LanguageSelector from './LanguageSelector';

// BackButton component (same as in other components)
const BackButton = () => {
  const navigate = useNavigate();
  const { language } = useLanguage();
  
  return (
    <button
      onClick={() => navigate(-1)}
      style={{
        background: 'none',
        border: 'none',
        fontSize: 24,
        cursor: 'pointer',
        color: '#888',
        padding: 8,
        borderRadius: 4,
        transition: 'color 0.2s',
        textDecoration: 'none'
      }}
      onMouseOver={(e) => e.target.style.color = '#333'}
      onMouseOut={(e) => e.target.style.color = '#888'}
      title={getTranslation('back', language)}
    >
      ←
    </button>
  );
};

// Breadcrumbs component
const Breadcrumbs = () => {
  const { language } = useLanguage();
  const location = window.location.pathname;
  const segments = location.split('/').filter(seg => seg);
  
  const crumbs = segments.map(seg => {
    const decodedSeg = decodeURIComponent(seg);
    return decodedSeg;
  });
  
  return (
    <div style={{ fontFamily: 'Nunito, Arial, sans-serif', fontWeight: 600, fontSize: window.innerWidth <= 600 ? 18 : 36, color: '#1e3a8a', textAlign: 'center', letterSpacing: 1 }}>
      {crumbs.map((seg, idx) => {
        let displayText;
        if (idx === 0 && seg === 'mecz') {
          displayText = getTranslation('menu', language);
        } else if (seg === 'turniej-rankingowy') {
          displayText = getTranslation('turniej-rankingowy', language);
        } else if (seg === 'dodaj') {
          displayText = getTranslation('dodaj-turniej', language);
        } else if (seg === 'szczegoly') {
          // Check if we came from the add tournament page by looking at URL parameters
          const urlParams = new URLSearchParams(window.location.search);
          if (urlParams.get('from') === 'dodaj') {
            displayText = getTranslation('dodaj-turniej', language);
          } else {
            displayText = getTranslation('szczegoly-turnieju', language);
          }
        } else if (seg === 'dodaj-runde') {
          displayText = getTranslation('dodaj-runde-breadcrumb', language);
        } else if (seg === 'mecz') {
          // Skip the intermediate 'mecz' segment
          return null;
        } else if (!isNaN(seg)) {
          // If it's a number (tournament ID), show "rundy"
          displayText = getTranslation('rundy', language);
        } else {
          displayText = getTranslation(seg, language);
        }
        
        return (
          <span key={idx}>
            {idx > 0 && <span style={{ margin: '0 8px' }}>/</span>}
            {displayText}
          </span>
        );
      }).filter(Boolean)}
    </div>
  );
};

const AddTournamentRound = () => {
  const { tournamentId } = useParams();
  const navigate = useNavigate();
  const { language } = useLanguage();
  const [tournament, setTournament] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  
  const [formData, setFormData] = useState({
    roundtype: '',
    opponent1: '',
    opponent2: '',
    opponent3: '',
    opponent4: '',
    playerscore: '',
    opponentscore: ''
  });

  // Fetch tournament details
  useEffect(() => {
    const fetchTournamentData = async () => {
      try {
        const token = sessionStorage.getItem('access_token');
        if (!token) {
          throw new Error('Brak tokenu autoryzacji. Zaloguj się ponownie.');
        }
        
        // Fetch tournament details
        const tournamentResponse = await fetch(`/api/tournaments/${tournamentId}`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
        
        if (!tournamentResponse.ok) {
          throw new Error(`Błąd ${tournamentResponse.status}: ${await tournamentResponse.text()}`);
        }
        
        const currentTournament = await tournamentResponse.json();
        setTournament(currentTournament);
        setLoading(false);
        
      } catch (err) {
        console.error('Fetch error:', err);
        setError(`Błąd połączenia: ${err.message}`);
        setLoading(false);
      }
    };

    fetchTournamentData();
  }, [tournamentId]);

  // Helper function to determine which opponent fields to show based on tournament type
  const getOpponentFieldsConfig = (numberofplayers) => {
    switch (numberofplayers) {
      case 1: // Single
        return {
          showOpponent1: true,
          showOpponent2: false,
          showOpponent3: false,
          showOpponent4: false,
          opponent4Label: getTranslation('rezerwowy', language)
        };
      case 2: // Dublety
        return {
          showOpponent1: true,
          showOpponent2: true,
          showOpponent3: false,
          showOpponent4: false,
          opponent4Label: getTranslation('rezerwowy', language)
        };
      case 3: // Triplety
        return {
          showOpponent1: true,
          showOpponent2: true,
          showOpponent3: true,
          showOpponent4: false,
          opponent4Label: getTranslation('rezerwowy', language)
        };
      case 4: // Triplety z rezerwowym
        return {
          showOpponent1: true,
          showOpponent2: true,
          showOpponent3: true,
          showOpponent4: true,
          opponent4Label: getTranslation('rezerwowy', language)
        };
      default:
        return {
          showOpponent1: false,
          showOpponent2: false,
          showOpponent3: false,
          showOpponent4: false,
          opponent4Label: getTranslation('rezerwowy', language)
        };
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear error when user starts typing
    if (error) {
      setError(null);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.roundtype) {
      setError(getTranslation('wybierz-typ-rundy', language));
      return;
    }
    
    if (!formData.playerscore || !formData.opponentscore) {
      setError(getTranslation('wprowadz-wyniki-dla-obu-druzyn', language));
      return;
    }
    
    const playerScore = parseInt(formData.playerscore);
    const opponentScore = parseInt(formData.opponentscore);
    
    if (isNaN(playerScore) || isNaN(opponentScore)) {
      setError(getTranslation('wyniki-musza-byc-liczbami', language));
      return;
    }
    
    if (playerScore < 0 || playerScore > 13 || opponentScore < 0 || opponentScore > 13) {
      setError(getTranslation('wyniki-musza-byc-miedzy-0-a-13', language));
      return;
    }
    
    if (playerScore === opponentScore) {
      setError('W petanque nie ma remisów! Wyniki obu drużyn muszą się różnić.');
      return;
    }
    
    // Check if opponent fields are filled based on tournament type
    const requiredOpponents = tournament.numberofplayers - 1;
    if (requiredOpponents >= 1 && !formData.opponent1.trim()) {
      setError(getTranslation('wprowadz-nazwe-przeciwnika1', language));
      return;
    }
    if (requiredOpponents >= 2 && !formData.opponent2.trim()) {
      setError(getTranslation('wprowadz-nazwe-przeciwnika2', language));
      return;
    }
    if (requiredOpponents >= 3 && !formData.opponent3.trim()) {
      setError(getTranslation('wprowadz-nazwe-przeciwnika3', language));
      return;
    }
    if (requiredOpponents >= 4 && !formData.opponent4.trim()) {
      setError(getTranslation('wprowadz-nazwe-rezerwowego', language));
      return;
    }
    
    setSubmitting(true);
    setError(null);
    
    try {
      const token = sessionStorage.getItem('access_token');
      
      const gameData = {
        roundtype: formData.roundtype,
        playerscore: playerScore,
        opponentscore: opponentScore,
        opponent1: formData.opponent1.trim() || null,
        opponent2: formData.opponent2.trim() || null,
        opponent3: formData.opponent3.trim() || null,
        opponent4: formData.opponent4.trim() || null,
        tournament: parseInt(tournamentId),
        numberofplayers: tournament.numberofplayers,
        player1: tournament.player1,
        player2: tournament.player2,
        player3: tournament.player3,
        player4: tournament.player4,
        gamedate: tournament.tournamentdate
      };
      
      const response = await fetch('/api/tournaments/games', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(gameData)
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Błąd ${response.status}: ${errorText}`);
      }
      
      // Navigate back to tournament details
      navigate(`/mecz/turniej-rankingowy/szczegoly/${tournamentId}`);
      
    } catch (err) {
      console.error('Submit error:', err);
      setError(`Błąd podczas zapisywania rundy: ${err.message}`);
    } finally {
      setSubmitting(false);
    }
  };

  const getRoundTypeDisplay = (roundtype) => {
    switch (roundtype) {
      case 'buchholz': return 'B';
      case 'puchar': return 'P';
      case 'grupa': return 'G';
      default: return roundtype || '-';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('pl-PL');
  };

  if (loading) {
    return (
      <div style={{ position: 'relative', minHeight: '100vh', padding: 20, background: '#f0f8ff' }}>
        <h1 style={{
          fontSize: window.innerWidth <= 600 ? '2.5rem' : '3.5rem',
          fontWeight: 'bold',
          color: '#7c3aed',
          margin: '0 0 16px 0',
          textAlign: 'center',
          fontFamily: 'Nunito, Arial, sans-serif'
        }}>
          {getTranslation('shootMe', language)}
        </h1>
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <p>{getTranslation('ladowanieDanych', language)}</p>
        </div>
      </div>
    );
  }

  if (error && !tournament) {
    return (
      <div style={{ position: 'relative', minHeight: '100vh', padding: 20, background: '#f0f8ff' }}>
        <h1 style={{
          fontSize: window.innerWidth <= 600 ? '2.5rem' : '3.5rem',
          fontWeight: 'bold',
          color: '#7c3aed',
          margin: '0 0 16px 0',
          textAlign: 'center',
          fontFamily: 'Nunito, Arial, sans-serif'
        }}>
          ShootMe
        </h1>
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <div style={{
            background: 'white',
            borderRadius: '12px',
            padding: '40px',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
            maxWidth: '600px',
            margin: '0 auto'
          }}>
            <h2 style={{
              color: '#1e3a8a',
              fontFamily: 'Nunito, Arial, sans-serif',
              marginBottom: '20px',
              fontSize: '1.5rem'
            }}>
              Błąd ładowania turnieju
            </h2>
            <p style={{ color: 'red', fontFamily: 'Nunito, Arial, sans-serif', marginBottom: '20px' }}>
              {error}
            </p>
            <button 
              onClick={() => window.location.reload()} 
              style={{
                background: '#7c3aed',
                color: 'white',
                border: 'none',
                padding: '10px 20px',
                borderRadius: '6px',
                cursor: 'pointer',
                fontFamily: 'Nunito, Arial, sans-serif',
                fontSize: '14px'
              }}
            >
              Spróbuj ponownie
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ position: 'relative', minHeight: '100vh', padding: 20, background: '#f0f8ff' }}>
      {/* Main Title */}
      <h1 style={{
        fontSize: window.innerWidth <= 600 ? '2.5rem' : '3.5rem',
        fontWeight: 'bold',
        color: '#7c3aed',
        margin: '0 0 16px 0',
        textAlign: 'center',
        fontFamily: 'Nunito, Arial, sans-serif'
      }}>
        {getTranslation('shootMe', language)}
      </h1>
      
      {/* Language Selector */}
      <div style={{
        position: 'absolute',
        top: '20px',
        right: '20px',
        zIndex: 1000
      }}>
        <LanguageSelector />
      </div>
      
      {/* Back button and breadcrumbs in horizontal layout */}
      <div style={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center', 
        gap: '20px',
        margin: '24px 0'
      }}>
        <BackButton />
        <Breadcrumbs />
      </div>

      {/* Tournament Info */}
      <div style={{ maxWidth: '800px', margin: '0 auto' }}>
        <div style={{
          background: 'white',
          borderRadius: '12px',
          padding: '20px',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
          marginBottom: '20px'
        }}>
          <h2 style={{
            color: '#1e3a8a',
            fontFamily: 'Nunito, Arial, sans-serif',
            marginBottom: '20px',
            fontSize: '1.5rem',
            textAlign: 'center'
          }}>
            {tournament.tournamentname}
          </h2>
          
          <div style={{
            display: 'grid',
            gridTemplateColumns: window.innerWidth <= 600 ? '1fr' : 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: '15px',
            fontFamily: 'Nunito, Arial, sans-serif',
            fontSize: '14px'
          }}>
            <div><strong>{getTranslation('data', language)}:</strong> {formatDate(tournament.tournamentdate)}</div>
            <div><strong>{getTranslation('miasto', language)}:</strong> {tournament.city || '-'}</div>
            <div><strong>{getTranslation('typ', language)}:</strong> {tournament.numberofplayers === 1 ? getTranslation('single', language) : tournament.numberofplayers === 2 ? getTranslation('dublety', language) : tournament.numberofplayers === 3 ? getTranslation('triplety', language) : getTranslation('triplety-z-rezerwowym', language)}</div>
          </div>
        </div>

        {/* Add Round Form */}
        <div style={{
          background: 'white',
          borderRadius: '12px',
          padding: '20px',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)'
        }}>
          <h3 style={{
            color: '#1e3a8a',
            fontFamily: 'Nunito, Arial, sans-serif',
            marginBottom: '20px',
            fontSize: '1.3rem',
            textAlign: 'center'
          }}>
            {getTranslation('dodaj-runde', language)}
          </h3>
          
          {error && (
            <div style={{
              background: '#fee2e2',
              border: '1px solid #fecaca',
              color: '#dc2626',
              padding: '12px',
              borderRadius: '6px',
              marginBottom: '20px',
              fontFamily: 'Nunito, Arial, sans-serif',
              fontSize: '14px'
            }}>
              {error}
            </div>
          )}
          
          <form onSubmit={handleSubmit} onInvalid={(e) => {
            e.preventDefault();
            const input = e.target;
            if (input.type === 'number') {
              if (input.value < 0 || input.value > 13) {
                setError(getTranslation('wyniki-musza-byc-miedzy-0-a-13', language));
              } else if (input.value === '') {
                setError(getTranslation('wprowadz-wyniki-dla-obu-druzyn', language));
              }
            }
          }}>
            {/* Round Type */}
            <div style={{ marginBottom: '20px' }}>
              <label style={{
                display: 'block',
                marginBottom: '8px',
                fontFamily: 'Nunito, Arial, sans-serif',
                fontWeight: '600',
                color: '#1e3a8a'
              }}>
                {getTranslation('typ-rundy', language)}:
              </label>
              <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap' }}>
                <label style={{ display: 'flex', alignItems: 'center', fontFamily: 'Nunito, Arial, sans-serif', cursor: 'pointer' }}>
                  <input
                    type="radio"
                    name="roundtype"
                    value="B"
                    checked={formData.roundtype === 'B'}
                    onChange={(e) => handleInputChange('roundtype', e.target.value)}
                    style={{ marginRight: '8px' }}
                  />
                  {getTranslation('runda-buchholza', language)}
                </label>
                <label style={{ display: 'flex', alignItems: 'center', fontFamily: 'Nunito, Arial, sans-serif', cursor: 'pointer' }}>
                  <input
                    type="radio"
                    name="roundtype"
                    value="P"
                    checked={formData.roundtype === 'P'}
                    onChange={(e) => handleInputChange('roundtype', e.target.value)}
                    style={{ marginRight: '8px' }}
                  />
                  {getTranslation('runda-pucharowa', language)}
                </label>
                <label style={{ display: 'flex', alignItems: 'center', fontFamily: 'Nunito, Arial, sans-serif', cursor: 'pointer' }}>
                  <input
                    type="radio"
                    name="roundtype"
                    value="G"
                    checked={formData.roundtype === 'G'}
                    onChange={(e) => handleInputChange('roundtype', e.target.value)}
                    style={{ marginRight: '8px' }}
                  />
                  {getTranslation('runda-grupowa', language)}
                </label>
              </div>
            </div>

            {/* Opponent Fields */}
            <div style={{ marginBottom: '20px' }}>
              <label style={{
                display: 'block',
                marginBottom: '8px',
                fontFamily: 'Nunito, Arial, sans-serif',
                fontWeight: '600',
                color: '#1e3a8a'
              }}>
                {getTranslation('przeciwnicy', language)}:
              </label>
              <div style={{ display: 'grid', gap: '12px' }}>
                {(() => {
                  const config = getOpponentFieldsConfig(tournament.numberofplayers);
                  return (
                    <>
                      {config.showOpponent1 && (
                        <input
                          type="text"
                          value={formData.opponent1}
                          onChange={(e) => handleInputChange('opponent1', e.target.value)}
                          placeholder={getTranslation('przeciwnik1', language)}
                          maxLength={30}
                          style={{
                            padding: '10px',
                            border: '1px solid #d1d5db',
                            borderRadius: '6px',
                            fontFamily: 'Nunito, Arial, sans-serif',
                            fontSize: '14px'
                          }}
                        />
                      )}
                      {config.showOpponent2 && (
                        <input
                          type="text"
                          value={formData.opponent2}
                          onChange={(e) => handleInputChange('opponent2', e.target.value)}
                          placeholder={getTranslation('przeciwnik2', language)}
                          maxLength={30}
                          style={{
                            padding: '10px',
                            border: '1px solid #d1d5db',
                            borderRadius: '6px',
                            fontFamily: 'Nunito, Arial, sans-serif',
                            fontSize: '14px'
                          }}
                        />
                      )}
                      {config.showOpponent3 && (
                        <input
                          type="text"
                          value={formData.opponent3}
                          onChange={(e) => handleInputChange('opponent3', e.target.value)}
                          placeholder={getTranslation('przeciwnik3', language)}
                          maxLength={30}
                          style={{
                            padding: '10px',
                            border: '1px solid #d1d5db',
                            borderRadius: '6px',
                            fontFamily: 'Nunito, Arial, sans-serif',
                            fontSize: '14px'
                          }}
                        />
                      )}
                      {config.showOpponent4 && (
                        <input
                          type="text"
                          value={formData.opponent4}
                          onChange={(e) => handleInputChange('opponent4', e.target.value)}
                          placeholder={config.opponent4Label}
                          maxLength={30}
                          style={{
                            padding: '10px',
                            border: '1px solid #d1d5db',
                            borderRadius: '6px',
                            fontFamily: 'Nunito, Arial, sans-serif',
                            fontSize: '14px'
                          }}
                        />
                      )}
                    </>
                  );
                })()}
              </div>
            </div>

            {/* Score Fields */}
            <div style={{ marginBottom: '20px' }}>
              <label style={{
                display: 'block',
                marginBottom: '8px',
                fontFamily: 'Nunito, Arial, sans-serif',
                fontWeight: '600',
                color: '#1e3a8a'
              }}>
                {getTranslation('wynik', language)}:
              </label>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px', maxWidth: '400px', margin: '0 auto' }}>
                <div>
                  <label style={{
                    display: 'block',
                    marginBottom: '8px',
                    fontFamily: 'Nunito, Arial, sans-serif',
                    fontSize: '14px',
                    fontWeight: '500',
                    color: '#374151'
                  }}>
                    {getTranslation('moja-druzyna', language)}
                  </label>
                  <input
                    type="number"
                    value={formData.playerscore}
                    onChange={(e) => handleInputChange('playerscore', e.target.value)}
                    min="0"
                    max="13"
                    title="Wynik musi być między 0 a 13"
                    style={{
                      padding: '16px',
                      border: '2px solid #d1d5db',
                      borderRadius: '8px',
                      fontFamily: 'Nunito, Arial, sans-serif',
                      fontSize: '18px',
                      fontWeight: '600',
                      width: '100%',
                      textAlign: 'center',
                      backgroundColor: '#f9fafb',
                      transition: 'border-color 0.2s, background-color 0.2s',
                      boxSizing: 'border-box'
                    }}
                    onFocus={(e) => {
                      e.target.style.borderColor = '#7c3aed';
                      e.target.style.backgroundColor = '#ffffff';
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = '#d1d5db';
                      e.target.style.backgroundColor = '#f9fafb';
                    }}
                  />
                </div>
                <div>
                  <label style={{
                    display: 'block',
                    marginBottom: '8px',
                    fontFamily: 'Nunito, Arial, sans-serif',
                    fontSize: '14px',
                    fontWeight: '500',
                    color: '#374151'
                  }}>
                    {getTranslation('przeciwnicy', language)}
                  </label>
                  <input
                    type="number"
                    value={formData.opponentscore}
                    onChange={(e) => handleInputChange('opponentscore', e.target.value)}
                    min="0"
                    max="13"
                    title="Wynik musi być między 0 a 13"
                    style={{
                      padding: '16px',
                      border: '2px solid #d1d5db',
                      borderRadius: '8px',
                      fontFamily: 'Nunito, Arial, sans-serif',
                      fontSize: '18px',
                      fontWeight: '600',
                      width: '100%',
                      textAlign: 'center',
                      backgroundColor: '#f9fafb',
                      transition: 'border-color 0.2s, background-color 0.2s',
                      boxSizing: 'border-box'
                    }}
                    onFocus={(e) => {
                      e.target.style.borderColor = '#7c3aed';
                      e.target.style.backgroundColor = '#ffffff';
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = '#d1d5db';
                      e.target.style.backgroundColor = '#f9fafb';
                    }}
                  />
                </div>
              </div>
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={submitting}
              style={{
                background: submitting ? '#9ca3af' : '#7c3aed',
                color: 'white',
                border: 'none',
                padding: '12px 24px',
                borderRadius: '8px',
                cursor: submitting ? 'not-allowed' : 'pointer',
                fontFamily: 'Nunito, Arial, sans-serif',
                fontSize: '16px',
                fontWeight: '600',
                width: '100%',
                transition: 'background 0.2s'
              }}
            >
              {submitting ? getTranslation('zapisywanie', language) : getTranslation('zapisz-runde', language)}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default AddTournamentRound;
